"use client";

import { useState, useCallback, useRef, useMemo } from "react";
import type { PreOrderFlow } from "@/app/api/types/preOrder";
import { useFeatureFlags } from "@/query/hooks/useFeatureFlags";
import { filterDefsByMode } from "../config/customColumns";
import { useRecapModeOptional } from "../contexts/RecapContext";
import type { ZeroQuantityField } from "../services/validationService";
import {
  collectConfirmWarnings,
  type ConfirmWarning,
} from "../services/confirmWarnings";

export interface ConfirmWarningInput {
  /** All pending flows (zero-quantity + required custom fields). */
  flows: PreOrderFlow[];
  /** Purchase-category pending flows for the zero-price check (defaults to `flows`). */
  priceFlows?: PreOrderFlow[];
  /** Whether to run the purchase-only zero-price check. */
  isPurchase: boolean;
}

/**
 * The single pre-confirm warning gate. Runs every flow-level confirm check
 * (required custom fields, zero quantity, zero price) and, if anything trips,
 * shows ONE aggregated dialog naming each line + field before letting the
 * action proceed. Replaces the old per-warning hooks/contexts/dialogs.
 *
 *   const { checkAndConfirm, dialogProps } = useConfirmWarnings();
 *   checkAndConfirm({ flows, isPurchase }, () => confirmAction());
 *   <ConfirmWarningsDialog {...dialogProps} />
 */
export function useConfirmWarnings() {
  const [isOpen, setIsOpen] = useState(false);
  const [warnings, setWarnings] = useState<ConfirmWarning[]>([]);
  const pendingAction = useRef<(() => void) | null>(null);

  // Mirror the column-registry visibility rules: don't warn about a field the
  // user has no UI to fill. While flags load, exclude the optional columns to
  // avoid a transient false positive (only quantity is always checked).
  const { data: flags, isLoading: flagsLoading } = useFeatureFlags();
  // Mode-scoped like the columns themselves: don't warn about a required
  // field/column that isn't rendered on this surface (mode null = transport).
  const mode = useRecapModeOptional();
  const zeroFields = useMemo<ZeroQuantityField[]>(() => {
    const fields: ZeroQuantityField[] = ["quantity"];
    // Transport (mode === null) never shows the unit column regardless of the
    // flag, so skip the unit validation entirely on that surface.
    if (!flagsLoading && mode !== null && flags?.showUnitRecap !== false)
      fields.push("unit_id");
    if (!flagsLoading && flags?.showWeightRecap !== false)
      fields.push("weight");
    return fields;
  }, [flagsLoading, mode, flags?.showUnitRecap, flags?.showWeightRecap]);
  const customFieldDefs = useMemo(
    () => filterDefsByMode(flags?.preOrderFlowCustomFields ?? [], mode),
    [flags?.preOrderFlowCustomFields, mode],
  );
  const customColumnDefs = useMemo(
    () => filterDefsByMode(flags?.preOrderFlowCustomColumns ?? [], mode),
    [flags?.preOrderFlowCustomColumns, mode],
  );

  const checkAndConfirm = useCallback(
    (input: ConfirmWarningInput, action: () => void) => {
      const found = collectConfirmWarnings({
        flows: input.flows,
        priceFlows: input.priceFlows,
        isPurchase: input.isPurchase,
        zeroFields,
        customFieldDefs,
        customColumnDefs,
      });
      if (found.length > 0) {
        setWarnings(found);
        pendingAction.current = action;
        setIsOpen(true);
      } else {
        action();
      }
    },
    [zeroFields, customFieldDefs, customColumnDefs],
  );

  const handleContinue = useCallback(() => {
    setIsOpen(false);
    pendingAction.current?.();
    pendingAction.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    pendingAction.current = null;
  }, []);

  return {
    checkAndConfirm,
    dialogProps: {
      isOpen,
      warnings,
      onClose: handleCancel,
      onContinue: handleContinue,
    },
  };
}
